'use client';

import { SliderArrow } from '@/components/SliderArrow';
import { Default_Image } from '@/constants';
import { WidgetViewOptions } from '@/enums';
import { useElementOnScreen } from '@/hooks/intersectionObserver';
import { IconArrowLeftDouble } from '@/icons/IconArrowLeftDouble';
import { IconArrowRightDouble } from '@/icons/IconArrowRightDouble';
import { IWidgetSectionProperty, IWidgetTypeItems } from '@/interfaces/homepage';
import Image from 'next/image';
import { useEffect, useState } from 'react';
import { Autoplay, FreeMode, Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/free-mode';
import 'swiper/css/navigation';
import CustomLink from '@/components/CustomLink';

const Slider = ({
  children,
  className,
  spacing = 4,
  autoPlayTimer,
  dataTestId = 'widgets-slider',
}: {
  children: React.ReactNode;
  className?: string;
  spacing?: number;
  autoPlayTimer: number;
  dataTestId?: string;
}) => {
  const [isMounted, setIsMounted] = useState<boolean>(false);

  const { containerRef, isVisible } = useElementOnScreen({
    root: null,
    rootMargin: '-100px',
    threshold: 0.0,
  });

  useEffect(() => {
    if (isVisible) {
      setIsMounted(true);
      containerRef?.current?.swiper?.autoplay.start();
    } else {
      containerRef?.current?.swiper?.autoplay.stop();
    }
  }, [isVisible, containerRef]);

  const swiperInternalComponent = (children: any[]) => {
    return (
      <Swiper
        key={isMounted.toString()}
        ref={containerRef}
        slidesPerView={'auto'}
        direction="vertical"
        spaceBetween={spacing}
        wrapperClass="flex h-full"
        modules={[FreeMode, Navigation, Autoplay]}
        autoHeight={true}
        freeMode={{
          enabled: true,
          sticky: true,
        }}
        centeredSlides={true}
        autoplay={{
          delay: autoPlayTimer - 1000,
        }}
        loop={children.length >= 8}
        navigation={{
          prevEl: '.widgets-swipe-prev',
          nextEl: '.widgets-swipe-next',
        }}
        breakpoints={{
          540: {
            slidesPerView: 2,
            centeredSlides: false,
            direction: 'horizontal',
            autoplay: {
              delay: autoPlayTimer,
            },
          },
          1024: {
            slidesPerView: 3,
            centeredSlides: false,
            direction: 'horizontal',
            autoplay: {
              delay: autoPlayTimer,
            },
          },
          1200: {
            slidesPerView: 4,
            centeredSlides: false,
            direction: 'horizontal',
            autoplay: {
              delay: autoPlayTimer,
            },
          },
        }}
        data-testid={`${dataTestId}-swiper`}
      >
        {children.map((child, index) => {
          return (
            <SwiperSlide key={`_${child.key}_${index}_`} data-testid={`${dataTestId}-slide-${index}`}>
              {child}
            </SwiperSlide>
          );
        })}
      </Swiper>
    );
  };

  return Array.isArray(children) ? (
    <div className={`${className} relative`} data-testid={dataTestId}>
      {isMounted ? swiperInternalComponent(children) : swiperInternalComponent(children.length > 4 ? children.slice(0, 4) : children)}

      <div className="widgets-swipe-prev swipe-prev absolute bottom-0 left-0 right-0 top-0" data-testid={`${dataTestId}-prev-arrow`}>
        <SliderArrow className="left-3" data-testid={`${dataTestId}-prev-arrow-button`}>
          <IconArrowLeftDouble className="h-8 w-8 text-gray-600 xs:text-white" />
        </SliderArrow>
      </div>
      <div className="widgets-swipe-next swipe-next absolute bottom-0 left-0 right-0 top-0" data-testid={`${dataTestId}-next-arrow`}>
        <SliderArrow className="right-3" data-testid={`${dataTestId}-next-arrow-button`}>
          <IconArrowRightDouble className="h-8 w-8 text-gray-600 xs:text-white" />
        </SliderArrow>
      </div>
    </div>
  ) : (
    <></>
  );
};

const WidgetCard = ({
  widget,
  spacing,
  className,
  dataTestId,
}: {
  widget?: IWidgetTypeItems;
  spacing: number;
  className: string;
  dataTestId?: string;
}) => (
  <div key={widget?.ImageUrl} className={`aspect-square ${className}`} style={{ padding: spacing / 2 }} data-testid={dataTestId}>
    <CustomLink href={widget?.PageUrl || ''} className={`relative flex aspect-square`} data-testid={`${dataTestId}-link`}>
      <Image
        src={widget?.ImageUrl !== '' ? widget?.ImageUrl! : Default_Image}
        alt={widget?.altText || widget?.Text || ''}
        fill
        quality={100}
        sizes="100%"
        unoptimized={true}
        data-testid={`${dataTestId}-image`}
      />
      <span className="text-dark absolute bottom-6 w-full text-center text-lg font-bold uppercase tracking-wider" data-testid={`${dataTestId}-text`}>
        {widget?.Text}
      </span>
    </CustomLink>
  </div>
);

const getWidgets = (widgets: IWidgetTypeItems[], spacing: number, parentTestId: string) => {
  return widgets?.map((widget, index) => (
    <WidgetCard
      className={spacing > 0 ? 'widget-card-width-style w-full bg-[#ebeff1]' : 'w-full bg-[#ebeff1]'}
      key={widget.ImageUrl}
      spacing={spacing}
      widget={widget}
      dataTestId={`${parentTestId}-widget-card-${index}`}
    />
  ));
};

const Widgets = ({
  className,
  widgetProperties,
  autoPlayTimer,
  dataTestId = 'widgets',
}: {
  widgetProperties?: IWidgetSectionProperty;
  className?: string;
  autoPlayTimer: number;
  dataTestId?: string;
}) => {
  const spacing = 8;

  return (
    <>
      {widgetProperties?.Items?.length ? (
        <div className={`widgets ${className}`} data-testid={dataTestId}>
          {widgetProperties?.DisplayType === WidgetViewOptions.Slider ? (
            <Slider
              spacing={spacing}
              autoPlayTimer={autoPlayTimer}
              className="slider widget-slider-style max-xs:aspect-square"
              dataTestId={`${dataTestId}-slider`}
            >
              {getWidgets(widgetProperties?.Items, 0, dataTestId)}
            </Slider>
          ) : (
            <div className="-m-2 flex flex-wrap pl-2" data-testid={`${dataTestId}-grid-container`}>
              {getWidgets(widgetProperties?.Items, spacing, dataTestId)}
            </div>
          )}
        </div>
      ) : (
        <></>
      )}
      <style jsx global>{`
        .swiper-vertical {
          height: 100%;
        }
      `}</style>
    </>
  );
};

export default Widgets;
